import { FaBookOpen, FaVideo, FaCertificate, FaRocket, FaArrowRight, FaStar, FaUsers, FaGraduationCap } from "react-icons/fa";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import api from "../../services/api";
import { getPublicCourses } from "../../State/Course/Action";

export default function LandingPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const courseListResponse = useSelector((store) => store.course.courseListResponse);
  const publicCourses = Array.isArray(courseListResponse?.courses)
    ? courseListResponse.courses
    : Array.isArray(courseListResponse)
    ? courseListResponse
    : [];

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;
  const totalPages = Math.max(1, Math.ceil(publicCourses.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pagedCourses = publicCourses.slice(startIdx, endIdx);

  // Server-side pagination (fallbacks to Redux data if API doesn't provide pages)
  const [serverPage, setServerPage] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [serverCourses, setServerCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchServerPage = async (page) => {
    try {
      setIsLoading(true);
      const res = await api.get(`/courses/public?page=${page}`);
      const data = res.data;
      const list = Array.isArray(data?.courses) ? data.courses : (Array.isArray(data) ? data : []);
      setServerCourses(list);
      const total = Number(data?.totalPages) || 1;
      setServerTotalPages(total);
    } catch (e) {
      // Fallback to Redux if server paging fails
      setServerCourses(publicCourses);
      setServerTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServerPage(serverPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [publicCourses.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-primary-200 to-secondary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-gradient-to-r from-secondary-200 to-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-gradient-to-r from-accent-200 to-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float" style={{ animationDelay: '4s' }}></div>
        </div>

        <div className="relative text-center py-32 px-6 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-primary-100 to-secondary-100 text-primary-700 border border-primary-200">
              🚀 Join 50,000+ learners worldwide
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-gray-900 via-primary-700 to-secondary-700 bg-clip-text text-transparent mb-6"
          >
            Learn Anything.
            <br />
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Anytime. Anywhere.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed"
          >
            Vicademics is your all-in-one online course platform with interactive
            videos, expert instructors, and industry-recognized certifications.
            Transform your career with world-class education.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/browse-courses" className="btn-primary inline-flex items-center gap-2">
              Start Learning Free
              <FaArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/about" className="btn-outline">
              Learn More
            </Link>
          </motion.div>

          {/* Stats */}
          {/* <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-4xl mx-auto"
          >
            {[
              { icon: FaUsers, number: "50K+", label: "Active Students" },
              { icon: FaBookOpen, number: "500+", label: "Courses" },
              { icon: FaGraduationCap, number: "100+", label: "Expert Instructors" },
              { icon: FaStar, number: "4.9/5", label: "Average Rating" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="card-modern p-6 text-center"
              >
                <stat.icon className="text-3xl mx-auto mb-3 text-primary-600" />
                <div className="text-2xl font-bold text-gray-900">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div> */}
        </div>
      </section>

      {/* Public Courses Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Explore Popular Courses</h2>
            <Link to="/browse-courses" className="btn-outline inline-flex items-center gap-2">
              View All <FaArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {isLoading ? (
            <div className="text-center card-modern p-8">Loading courses...</div>
          ) : (serverCourses.length === 0 && publicCourses.length === 0) ? (
            <div className="text-center card-modern p-8">
              <p className="text-gray-600">No public courses available right now. Please check back later.</p>
            </div>
          ) : (
            <div>
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {(serverCourses.length ? serverCourses : pagedCourses).map((course) => (
                  <div
                    key={course.id}
                    className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition border border-gray-200 cursor-pointer"
                    onClick={() => {
                      const slug = (course.name || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
                      navigate(`/course-detail/${course.id}/${slug}`);
                    }}
                  >
                    <img
                      src={course.thumbnailUrl}
                      alt={course.name}
                      className="w-full h-40 object-cover rounded-md mb-3"
                    />
                    <div className="mb-2 text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <FaBookOpen className="text-indigo-500" /> {course.name}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-2">{course.description}</p>
                    <div className="flex items-center gap-2">
                      {course.discountAmount ? (
                        <>
                          <span className="text-sm text-gray-500 line-through">₹{course.price || 0}</span>
                          <span className="text-base font-bold text-indigo-600">
                            ₹{Math.max(0, (course.price || 0) - (course.discountAmount || 0))}
                          </span>
                          <span className="text-xs text-green-600 font-medium">(₹{course.discountAmount} Off)</span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-indigo-600">₹{course.price || 0}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {(serverTotalPages > 1 ? serverTotalPages : totalPages) > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    onClick={() => {
                      if (serverTotalPages > 1) setServerPage((p) => Math.max(0, p - 1));
                      else setCurrentPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={(serverTotalPages > 1 ? serverPage === 0 : currentPage === 1)}
                  >
                    Previous
                  </button>
                  {Array.from({ length: serverTotalPages > 1 ? serverTotalPages : totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`px-3 py-2 text-sm rounded-md border ${
                        (serverTotalPages > 1 ? pageNum === serverPage + 1 : pageNum === currentPage)
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        if (serverTotalPages > 1) setServerPage(pageNum - 1);
                        else setCurrentPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    onClick={() => {
                      if (serverTotalPages > 1) setServerPage((p) => Math.min(serverTotalPages - 1, p + 1));
                      else setCurrentPage((p) => Math.min(totalPages, p + 1));
                    }}
                    disabled={(serverTotalPages > 1 ? serverPage === serverTotalPages - 1 : currentPage === totalPages)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Vicademics</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of online education with our cutting-edge platform
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FaBookOpen,
                title: "Expert Courses",
                description: "Curated content by industry experts across all domains and skill levels.",
                gradient: "from-blue-500 to-cyan-500"
              },
              {
                icon: FaVideo,
                title: "Interactive Videos",
                description: "Engaging video tutorials with real-world projects and hands-on exercises.",
                gradient: "from-purple-500 to-pink-500"
              },
              {
                icon: FaCertificate,
                title: "Certifications",
                description: "Earn industry-recognized certificates and boost your career profile.",
                gradient: "from-green-500 to-emerald-500"
              },
              {
                icon: FaRocket,
                title: "Fast Learning",
                description: "Learn at your own pace with lifetime access and personalized paths.",
                gradient: "from-orange-500 to-red-500"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="card-gradient p-8 text-center group"
              >
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${feature.gradient} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="text-3xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-6 bg-gradient-to-r from-primary-50 via-secondary-50 to-accent-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Our <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Students</span> Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of successful learners who transformed their careers
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Bhaskar Sahu",
                role: "Technical Lead at SAP Labs",
                content: "Vicademics helped me switch careers and land my dream job at SAP Labs. The tutorials are top-notch and the instructors are world-class!",
                rating: 5
              },
              {
                name: "Gautam Kumar",
                role: "Tech Lead at Wells Fargo",
                content: "The hands-on projects and real-world applications made all the difference. I went from beginner to expert in just 3 months.",
                rating: 5
              },
              {
                name: "Prakash Verma",
                role: "Senior Software Engineer at Accenture",
                content: "The community support and mentorship opportunities are incredible. Vicademics doesn't just teach skills, it builds careers.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className="card-modern p-8"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-lg" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-primary-600">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="card-gradient p-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Ready to Transform Your Career?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of learners who are already building their dream careers with Vicademics
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/browse-courses" className="btn-primary inline-flex items-center gap-2">
                Browse Courses
                <FaArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/register" className="btn-secondary">
                Create Free Account
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
